import { skipToken } from "@reduxjs/toolkit/query";
import { getProduct } from "src/features/products/products.js";
import {
	useGetPowerBiConnectionStatusQuery,
	useGetPowerBiReportsQuery,
} from "src/features/powerBiReports/api/powerBiReportsApi.js";
import { toSetupStatus } from "src/features/powerBiReports/setup/powerBiSetupStatus.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import {
	type PowerBiWidgetState,
	PowerBiWidgetView,
} from "./PowerBiWidgetView.js";

export const PowerBiWidget = () => {
	const companyDomain = useLoadedCompanyDomain();
	// WHY currentData: `data` keeps the previous domain's answer through a domain switch.
	const { currentData: connection, isError: isStatusError } =
		useGetPowerBiConnectionStatusQuery(companyDomain.id);
	// WHY gated on the cheap status call: the reports call queries the customer's Power BI.
	const { currentData: reports, error } = useGetPowerBiReportsQuery(
		connection?.connected ? companyDomain.id : skipToken,
	);

	const state: PowerBiWidgetState = isStatusError
		? { kind: "error" }
		: !connection
			? { kind: "loading" }
			: !connection.connected
				? { kind: "notConnected" }
				: toWidgetState(
						// WHY not isFetching: a just-started request is still uninitialized for a render, which would flash the error state.
						toSetupStatus({ reports, isLoading: !reports && !error, error }),
					);

	return (
		<PowerBiWidgetView
			state={state}
			pagePath={getProduct("powerBi").path(companyDomain)}
		/>
	);
};

const toWidgetState = (
	status: ReturnType<typeof toSetupStatus>,
): PowerBiWidgetState => {
	switch (status.kind) {
		case "ready":
			return { kind: "ready", reports: status.reports };
		case "setupIncomplete":
			return { kind: "setupIncomplete" };
		// WHY: the status call said connected; a 404 now means it was removed in between.
		case "notConnected":
			return { kind: "notConnected" };
		default:
			return { kind: status.kind };
	}
};
